/**
 * Integrations API Service
 * 
 * Handles all integration-related API operations including:
 * - Product types (available products)
 * - Product instances (configured integrations per company)
 * - Connection testing
 * - Sync operations
 */

import { apiClient } from './apiClient';
import type { ProductType, ProductInstance, AddProductRequest, UpdateProductRequest } from '@/types/product';

export interface ProductTypeDto {
  id: string;
  code: ProductType;
  name: string;
  description: string | null;
  category: 'financial' | 'hr' | 'project' | 'resource';
  icon: string | null;
  features: string[];
  requiresProducts: string[] | null;
  displayOrder: number;
  platformProductId: string | null;
}

export interface ExternalInstanceDto {
  id: string;
  name: string;
  productId: string;
  isActive: boolean;
  reservedByCompanyId?: string | null;
  reservedByCompanyName?: string | null;
}

export interface ProductInstanceDto {
  id: string;
  companyId: string;
  productType: ProductType;
  productTypeName: string;
  productTypeCategory: string;
  productTypeIcon: string | null;
  instanceName: string;
  connectionStatus: 'connected' | 'not_connected' | 'error' | 'not_configured';
  apiEndpoint: string | null;
  lastSync: string | null;
  lastTestDate: string | null;
  lastTestStatus: 'success' | 'failed' | null;
  syncFrequency: 'manual' | 'hourly' | 'daily' | 'weekly';
  autoSync: boolean;
  config: Record<string, unknown> | null;
  errorMessage: string | null;
  autoConnectedByInstanceId: string | null;
  externalInstanceId: string | null;
  externalInstanceIdSecondary: string | null;
  defaultProjectId: string | null;
  defaultCostCentreId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TestConnectionResultDto {
  success: boolean;
  message: string;
  testedAt: string;
  systemInfo?: {
    version?: string;
    instanceName?: string;
    recordsAvailable?: number;
  };
}

export interface SyncResultDto {
  productInstanceId: string;
  status: string;
  recordsImported: number;
  recordsUpdated: number;
  recordsFailed: number;
  startedAt: string;
  completedAt: string;
  errors?: string[];
  warnings?: string[];
}

/**
 * Get all available product types
 */
export async function getProductTypes(): Promise<ProductTypeDto[]> {
  return apiClient.get<ProductTypeDto[]>('/producttypes');
}

/**
 * Get a specific product type by ID
 */
export async function getProductTypeById(id: string): Promise<ProductTypeDto> {
  return apiClient.get<ProductTypeDto>(`/producttypes/${id}`);
}

/**
 * Get a specific product type by code
 */
export async function getProductTypeByCode(code: string): Promise<ProductTypeDto> {
  return apiClient.get<ProductTypeDto>(`/producttypes/by-code/${code}`);
}

/**
 * Get product types by category
 */
export async function getProductTypesByCategory(category: string): Promise<ProductTypeDto[]> {
  return apiClient.get<ProductTypeDto[]>(`/producttypes/by-category/${category}`);
}

export interface AccessProjectsCompanyDto {
  id: string;
  name: string;
}

export interface AccessPlannerCompanyDto {
  id: string;
  name: string;
}

/**
 * Get external instances for a product type from Workspace API
 */
export async function getExternalInstances(productCode: string, companyId?: string): Promise<ExternalInstanceDto[]> {
  const query = companyId ? `?requestingCompanyId=${encodeURIComponent(companyId)}` : '';
  return apiClient.get<ExternalInstanceDto[]>(`/producttypes/${productCode}/external-instances${query}`);
}

/**
 * Get active companies from Access Projects for a given instance
 */
export async function getAccessProjectsCompanies(instanceId: string): Promise<AccessProjectsCompanyDto[]> {
  return apiClient.get<AccessProjectsCompanyDto[]>(
    `/integrations/access-projects/companies?instanceId=${encodeURIComponent(instanceId)}`
  );
}

/**
 * Get active companies from Access Planner for a given instance
 */
export async function getAccessPlannerCompanies(instanceId: string): Promise<AccessPlannerCompanyDto[]> {
  return apiClient.get<AccessPlannerCompanyDto[]>(
    `/integrations/access-planner/companies?instanceId=${encodeURIComponent(instanceId)}`
  );
}

/**
 * Get all product instances for a company
 */
export async function getProductInstances(companyId: string): Promise<ProductInstanceDto[]> {
  return apiClient.get<ProductInstanceDto[]>(`/companies/${companyId}/product-instances`);
}

/**
 * Get a specific product instance
 */
export async function getProductInstance(companyId: string, instanceId: string): Promise<ProductInstanceDto> {
  return apiClient.get<ProductInstanceDto>(`/companies/${companyId}/product-instances/${instanceId}`);
}

/**
 * Create a new product instance for a company
 */
export async function createProductInstance(
  companyId: string,
  request: AddProductRequest
): Promise<ProductInstanceDto> {
  return apiClient.post<ProductInstanceDto, AddProductRequest>(
    `/companies/${companyId}/product-instances`,
    request
  );
}

/**
 * Update an existing product instance
 */
export async function updateProductInstance(
  companyId: string,
  instanceId: string,
  request: UpdateProductRequest
): Promise<ProductInstanceDto> {
  return apiClient.put<ProductInstanceDto, UpdateProductRequest>(
    `/companies/${companyId}/product-instances/${instanceId}`,
    request
  );
}

/**
 * Delete a product instance
 */
export async function deleteProductInstance(companyId: string, instanceId: string): Promise<void> {
  await apiClient.delete(`/companies/${companyId}/product-instances/${instanceId}`);
}

/**
 * Test connection for a product instance
 */
export async function testProductConnection(
  companyId: string,
  instanceId: string
): Promise<TestConnectionResultDto> {
  return apiClient.post<TestConnectionResultDto, Record<string, never>>(
    `/companies/${companyId}/product-instances/${instanceId}/test-connection`,
    {}
  );
}

/**
 * Trigger sync for a product instance
 */
export async function syncProductInstance(
  companyId: string,
  instanceId: string
): Promise<SyncResultDto> {
  return apiClient.post<SyncResultDto, Record<string, never>>(
    `/companies/${companyId}/product-instances/${instanceId}/sync`,
    {}
  );
}

/**
 * Convert ProductInstanceDto to ProductInstance (frontend type)
 */
export function mapProductInstanceDtoToProductInstance(dto: ProductInstanceDto): ProductInstance {
  return {
    id: dto.id,
    productType: dto.productType,
    instanceName: dto.instanceName,
    companyId: dto.companyId,
    connectionStatus: dto.connectionStatus,
    apiEndpoint: dto.apiEndpoint,
    lastSync: dto.lastSync ? new Date(dto.lastSync) : null,
    lastTestDate: dto.lastTestDate ? new Date(dto.lastTestDate) : null,
    lastTestStatus: dto.lastTestStatus,
    syncFrequency: dto.syncFrequency,
    autoSync: dto.autoSync,
    config: dto.config || {},
    errorMessage: dto.errorMessage,
    autoConnectedByInstanceId: dto.autoConnectedByInstanceId,
    externalInstanceId: dto.externalInstanceId,
    externalInstanceIdSecondary: dto.externalInstanceIdSecondary,
    createdAt: new Date(dto.createdAt),
    updatedAt: new Date(dto.updatedAt),
  };
}

/**
 * Get product instances for a company and map to frontend types
 */
export async function getProductInstancesForCompany(companyId: string): Promise<ProductInstance[]> {
  const dtos = await getProductInstances(companyId);
  return dtos.map(mapProductInstanceDtoToProductInstance);
}
